/** Chat send / actions — every send goes through the protocol-v2 Messenger. */

import type {
  Contact,
  Conversation,
  DisappearTimerSec,
  GroupRecord,
  MessageEvent,
} from "@veto/domain";
import {
  applyReaction,
  buildForwardedMessage,
  createEventId,
  expireAt,
  listPreview,
  togglePinned,
  toggleStarred,
  tombstoneEveryone,
} from "@veto/domain";
import {
  CONTENT_TYPE_ATTACHMENT,
  CONTENT_TYPE_DELETE,
  CONTENT_TYPE_EDIT,
  CONTENT_TYPE_GROUP,
  CONTENT_TYPE_REACTION,
  CONTENT_TYPE_SIGNAL,
  CONTENT_TYPE_TEXT,
  encodeSignal,
  prepareAttachmentPayload,
  scrubImageMetadata,
  stickerRefLabel,
  stickerWireBody,
  validateAttachmentFile,
} from "@veto/protocol";
import { getGroupByConversation, upsertConversation } from "@veto/storage";
import {
  DirectPeerLink,
  iceServersWithMintedTurn,
  type DirectPeerLink as PeerLink,
} from "@veto/transport";
import { fanoutGroupMessage } from "../messaging/groupSend";
import { withDisappearSeconds } from "../messaging/disappearWire";
import { mediaBlobFor, rememberMediaBlob } from "../messaging/mediaBlobs";
import { directLinkAllowed, loadNetPrefs } from "../netPrefs";
import { prepareVoiceNote } from "../messaging/voiceNotes";
import { bodyFitsOneEnvelope, type Messenger } from "../messaging/messenger";

export function createChatActions(deps: {
  messenger: Messenger | null;
  activeId: string | null;
  activeConv: Conversation | null;
  activeContact: Contact | null;
  activeGroup: GroupRecord | null;
  contacts: Contact[];
  draft: string;
  replyTo: MessageEvent | null;
  editing: MessageEvent | null;
  disappearTimer: DisappearTimerSec;
  peers: Map<string, PeerLink>;
  voiceUrls: Map<string, string>;
  bumpVoice: () => void;
  /** Frames arriving on a direct peer link re-enter the inbound pipeline. */
  onPeerFrame: (data: string) => void;
  putMessage: (m: MessageEvent) => Promise<void>;
  listMessages: (id: string) => Promise<MessageEvent[]>;
  setDraft: (v: string) => void;
  setReplyTo: (m: MessageEvent | null) => void;
  setEditing: (m: MessageEvent | null) => void;
  setMessages: (m: MessageEvent[] | ((prev: MessageEvent[]) => MessageEvent[])) => void;
  setToast: (t: string | null) => void;
  refresh: () => Promise<void>;
}) {
  /** Direct chats: the counterpart, when sending is currently allowed. */
  function directTarget(): Contact | null {
    const c = deps.activeContact;
    if (!c || deps.activeConv?.type !== "direct") return null;
    if (c.blockedAt) {
      deps.setToast(`You blocked ${c.displayName} — unblock to send`);
      return null;
    }
    if (c.requestState === "pending" || deps.activeConv.requestPending) {
      deps.setToast("Accept this message request to reply");
      return null;
    }
    if (c.verificationState === "changed") {
      deps.setToast("Contact keys changed — confirm the safety number before sending");
      return null;
    }
    return c;
  }

  function markDelivery(
    eventId: string,
    status: MessageEvent["status"],
    transport?: MessageEvent["transport"],
  ) {
    deps.setMessages((m) =>
      m.map((x) =>
        x.eventId === eventId ? { ...x, status, transport: transport ?? x.transport } : x,
      ),
    );
  }

  async function persistAndShow(msg: MessageEvent, preview?: string) {
    await deps.putMessage(msg);
    deps.setMessages((m) => [...m, msg]);
    if (deps.activeConv) {
      await upsertConversation({
        ...deps.activeConv,
        lastMessagePreview: preview ?? (listPreview(msg.body) || msg.body),
        lastMessageAt: msg.createdAt,
        archived: false,
      });
    }
    await deps.refresh();
  }

  async function sendMessage() {
    const m = deps.messenger;
    if (!deps.draft.trim() || !deps.activeId || !m) return;
    const text = deps.draft.trim();
    if (!bodyFitsOneEnvelope(text)) {
      deps.setToast("Message too long — split it or send it as a document");
      return;
    }

    if (deps.activeConv?.type === "group" && deps.activeGroup) {
      if (deps.activeConv.requestPending) {
        deps.setToast("Join the group to write in it");
        return;
      }
      // Group edits have no wire form yet: sending would post the new text as another message.
      if (deps.editing) {
        deps.setToast("Editing isn't available in groups yet");
        return;
      }
      deps.setDraft("");
      await sendGroupText(text, deps.replyTo?.eventId);
      return;
    }

    const contact = directTarget();
    if (!contact) return;

    if (deps.editing) {
      const target = deps.editing;
      const out = await m.send({
        conversationId: deps.activeId,
        recipients: [contact],
        contentType: CONTENT_TYPE_EDIT,
        body: JSON.stringify({ op: "edit", target: target.eventId, body: text }),
      });
      const next = {
        ...target,
        body: text,
        editedAt: Date.now(),
        status: out.status,
        transport: out.transport,
      };
      await deps.putMessage(next);
      deps.setMessages((list) => list.map((x) => (x.eventId === target.eventId ? next : x)));
      deps.setEditing(null);
      deps.setDraft("");
      deps.setReplyTo(null);
      return;
    }

    const createdAt = Date.now();
    const timer = deps.disappearTimer;
    const wireBody = deps.replyTo
      ? `\u0001${deps.replyTo.eventId}\u0001${timer ? `\u0002${timer}\u0002` : ""}${text}`
      : timer
        ? `\u0002${timer}\u0002${text}`
        : text;
    deps.setDraft("");
    const eventId = createEventId();
    const msg: MessageEvent = {
      eventId,
      conversationId: deps.activeId,
      senderDevice: m.selfDevice,
      direction: "out",
      body: text,
      logicalCounter: createdAt,
      createdAt,
      status: "queued",
      contentType: CONTENT_TYPE_TEXT,
      replyToEventId: deps.replyTo?.eventId,
      disappearTimerSec: timer || undefined,
      expiresAt: expireAt(createdAt, timer),
    };
    // FR-MSG-001: durable local commit before any network activity.
    await persistAndShow(msg, text);
    deps.setReplyTo(null);
    const out = await m.send({
      conversationId: deps.activeId,
      recipients: [contact],
      contentType: CONTENT_TYPE_TEXT,
      body: wireBody,
      eventId,
      createdAt,
      tracksMessage: true,
    });
    await deps.putMessage({ ...msg, status: out.status, transport: out.transport });
    markDelivery(eventId, out.status, out.transport);
  }

  async function sendControl(contentType: number, op: Record<string, unknown>) {
    const m = deps.messenger;
    const contact = directTarget();
    if (!m || !contact || !deps.activeId) return;
    await m.send({
      conversationId: deps.activeId,
      recipients: [contact],
      contentType,
      body: JSON.stringify(op),
    });
  }

  async function sendReaction(msg: MessageEvent, emoji: string) {
    const m = deps.messenger;
    if (!m || !deps.activeId || deps.activeConv?.type !== "direct") return;
    const next = applyReaction(msg, emoji, m.selfDevice);
    await deps.putMessage(next);
    deps.setMessages((list) => list.map((x) => (x.eventId === msg.eventId ? next : x)));
    await sendControl(CONTENT_TYPE_REACTION, { op: "reaction", target: msg.eventId, emoji });
  }

  async function deleteForMe(msg: MessageEvent) {
    await deps.putMessage({ ...msg, deletedForMe: true });
    if (deps.activeId) deps.setMessages(await deps.listMessages(deps.activeId));
  }

  async function deleteForEveryone(msg: MessageEvent) {
    if (deps.activeConv?.type === "group") {
      // No group delete on the wire yet: it would only vanish here while everyone else keeps it.
      deps.setToast("Delete for everyone isn't available in groups yet — use Delete for me");
      return;
    }
    await deps.putMessage(tombstoneEveryone(msg));
    if (deps.activeId) deps.setMessages(await deps.listMessages(deps.activeId));
    await sendControl(CONTENT_TYPE_DELETE, { op: "delete", target: msg.eventId });
  }

  async function forwardMessage(msg: MessageEvent) {
    const m = deps.messenger;
    if (!m || !deps.activeId || deps.activeConv?.type === "group") {
      deps.setToast("Open a 1:1 chat to forward into");
      return;
    }
    if (msg.viewOnce) {
      deps.setToast("View-once messages cannot be forwarded");
      return;
    }
    const contact = directTarget();
    if (!contact) return;
    const fwd = buildForwardedMessage({
      source: msg,
      targetConversationId: deps.activeId,
      senderDevice: m.selfDevice,
    });
    await persistAndShow(fwd);
    const out = await m.send({
      conversationId: deps.activeId,
      recipients: [contact],
      contentType: CONTENT_TYPE_TEXT,
      body: fwd.body,
      eventId: fwd.eventId,
      createdAt: fwd.createdAt,
      tracksMessage: true,
    });
    await deps.putMessage({ ...fwd, status: out.status, transport: out.transport });
    markDelivery(fwd.eventId, out.status, out.transport);
    deps.setToast("Forwarded");
  }

  /**
   * Forward a copy of [msg] to each chosen chat (phone parity). Text goes anywhere; files only to
   * 1:1 chats, re-encrypted with a fresh key per chat (nothing is shared between conversations).
   */
  async function forwardTo(msg: MessageEvent, targets: Conversation[]) {
    const m = deps.messenger;
    if (!m || targets.length === 0) return;
    if (msg.viewOnce) {
      deps.setToast("View-once messages can't be forwarded");
      return;
    }
    const isFile = msg.contentType === CONTENT_TYPE_ATTACHMENT;
    let blob: Blob | null = null;
    if (isFile) {
      // From memory, not fetch(blob:…): the production CSP doesn't allow blob: connections.
      blob = mediaBlobFor(msg.eventId) ?? null;
      if (!blob) {
        deps.setToast("This file hasn't finished downloading yet");
        return;
      }
    }
    const text = msg.body.startsWith("↪ ") ? msg.body : `↪ ${msg.body}`;
    let sent = 0;
    for (const conv of targets) {
      try {
        const eventId = createEventId();
        const createdAt = Date.now();
        if (conv.type === "group") {
          if (isFile) continue;
          const group = await getGroupByConversation(conv.id);
          if (!group || conv.requestPending) continue;
          const out: MessageEvent = {
            eventId,
            conversationId: conv.id,
            senderDevice: m.selfDevice,
            direction: "out",
            body: text,
            logicalCounter: createdAt,
            createdAt,
            status: "queued",
            contentType: CONTENT_TYPE_GROUP,
          };
          await deps.putMessage(out);
          const res = await fanoutGroupMessage({ group, plaintext: text, contacts: deps.contacts, messenger: m, eventId, createdAt });
          await deps.putMessage({ ...out, status: res.status, transport: res.transport });
          await upsertConversation({ ...conv, lastMessagePreview: listPreview(text), lastMessageAt: createdAt });
        } else {
          const contact = deps.contacts.find((c) => c.id === conv.contactId);
          if (!contact || contact.blockedAt || contact.requestState === "pending") continue;
          let body = text;
          let label = text;
          let contentType = CONTENT_TYPE_TEXT;
          if (isFile && blob) {
            const kind = msg.body.startsWith("🎤")
              ? "voice"
              : msg.body.startsWith("🏷️")
                ? "sticker"
                : msg.body.startsWith("🎞️")
                  ? "gif"
                  : blob.type.startsWith("image/")
                    ? "image"
                    : "document";
            const fileName = msg.body.startsWith("📎 ") ? msg.body.slice(2).trim() : `forwarded.${(blob.type.split("/")[1] ?? "bin").split(";")[0]}`;
            const prepared = await prepareAttachmentPayload({ file: blob, fileName, mediaType: blob.type || "application/octet-stream", kind });
            body = prepared.sealedBody;
            label = prepared.label;
            contentType = CONTENT_TYPE_ATTACHMENT;
            deps.voiceUrls.set(eventId, URL.createObjectURL(blob));
            rememberMediaBlob(eventId, blob);
          }
          const out: MessageEvent = {
            eventId,
            conversationId: conv.id,
            senderDevice: m.selfDevice,
            direction: "out",
            body: label,
            logicalCounter: createdAt,
            createdAt,
            status: "queued",
            contentType,
          };
          await deps.putMessage(out);
          const res = await m.send({ conversationId: conv.id, recipients: [contact], contentType, body, eventId, createdAt, tracksMessage: true });
          await deps.putMessage({ ...out, status: res.status, transport: res.transport });
          await upsertConversation({ ...conv, lastMessagePreview: listPreview(label), lastMessageAt: createdAt });
        }
        sent++;
      } catch {
        /* one chat failing doesn't stop the others */
      }
    }
    deps.bumpVoice();
    if (deps.activeId && targets.some((c) => c.id === deps.activeId)) deps.setMessages(await deps.listMessages(deps.activeId));
    await deps.refresh();
    deps.setToast(sent === 0 ? "Couldn't forward — try again" : sent === 1 ? "Forwarded" : `Forwarded to ${sent} chats`);
  }

  async function starMessage(msg: MessageEvent) {
    const next = toggleStarred(msg);
    await deps.putMessage(next);
    deps.setMessages((m) => m.map((x) => (x.eventId === msg.eventId ? next : x)));
  }

  async function pinMessage(msg: MessageEvent) {
    const next = togglePinned(msg);
    await deps.putMessage(next);
    deps.setMessages((m) => m.map((x) => (x.eventId === msg.eventId ? next : x)));
  }

  /**
   * Opportunistic direct DataChannel with an accepted contact. Frames carry the
   * same sealed events as relays and go through the same authenticated pipeline.
   */
  async function tryOpenDirectPeer(opts?: { quiet?: boolean; onFailed?: () => void }) {
    const m = deps.messenger;
    const contact = deps.activeContact;
    if (!m || !deps.activeId || !contact || deps.activeConv?.type !== "direct") return;
    if (contact.blockedAt || contact.requestState === "pending") return;
    // SPEC §11: a direct link shows your network address to the contact.
    if (!loadNetPrefs().peerMesh) {
      if (!opts?.quiet)
        deps.setToast("Direct connections are off — turn them on in Settings → Network & delivery");
      return;
    }
    if (!directLinkAllowed(contact)) {
      if (!opts?.quiet)
        deps.setToast(
          "Direct connections are only for verified contacts — compare safety numbers first",
        );
      return;
    }
    const existing = deps.peers.get(deps.activeId);
    if (existing?.state === "open") {
      if (!opts?.quiet) deps.setToast("Direct connection already open");
      return;
    }
    if (existing?.state === "connecting") return;
    existing?.close();
    const convId = deps.activeId;
    const ice = await iceServersWithMintedTurn();
    const link = new DirectPeerLink(ice, {
      onMessage: (data) => deps.onPeerFrame(data),
      onState: (s) => {
        if (s === "open" && !opts?.quiet) deps.setToast("Direct peer channel open");
        if (s === "failed" || s === "closed") {
          if (deps.peers.get(convId) === link) deps.peers.delete(convId);
        }
        if (s === "failed") opts?.onFailed?.();
      },
    });
    deps.peers.set(convId, link);
    try {
      const offer = await link.createOfferPeer();
      await m.send({
        conversationId: convId,
        recipients: [contact],
        contentType: CONTENT_TYPE_SIGNAL,
        body: encodeSignal({ op: "webrtc-offer", sdp: offer.sdp ?? "" }),
      });
    } catch {
      link.close();
      if (deps.peers.get(convId) === link) deps.peers.delete(convId);
      if (!opts?.quiet) deps.setToast("Direct connection unavailable here — relays keep working");
    }
  }

  async function sendAttachment(
    file: File,
    kind: "image" | "document" | "sticker" | "gif",
    opts?: { viewOnce?: boolean },
  ) {
    const m = deps.messenger;
    if (!m || !deps.activeId) return;
    if (deps.activeConv?.type === "group") {
      deps.setToast("Group attachments are not supported yet — send it in a 1:1 chat");
      return;
    }
    const contact = directTarget();
    if (!contact) return;
    const check = validateAttachmentFile({
      fileName: file.name,
      mediaType: file.type || (kind === "document" ? "application/pdf" : "image/png"),
      size: file.size,
      kind: kind === "document" ? "document" : "image",
    });
    if (!check.ok) {
      deps.setToast(check.message);
      return;
    }
    try {
      const mediaType =
        file.type ||
        (kind === "document"
          ? "application/octet-stream"
          : kind === "gif"
            ? "image/gif"
            : kind === "sticker"
              ? "image/webp"
              : "image/jpeg");
      const wireKind = opts?.viewOnce ? "view-once" : kind;
      // Photos leave without GPS / camera metadata (phone parity, review M6); orientation kept.
      const clean =
        kind === "image" || kind === "sticker"
          ? new File([new Uint8Array(scrubImageMetadata(new Uint8Array(await file.arrayBuffer())))], file.name, {
              type: mediaType,
            })
          : file;
      const { sealedBody, label } = await prepareAttachmentPayload({
        file: clean,
        fileName: file.name,
        mediaType,
        kind: wireKind,
      });
      const timer = deps.disappearTimer;
      const eventId = createEventId();
      const createdAt = Date.now();
      deps.voiceUrls.set(eventId, URL.createObjectURL(file));
      rememberMediaBlob(eventId, clean);
      deps.bumpVoice();
      const msg: MessageEvent = {
        eventId,
        conversationId: deps.activeId,
        senderDevice: m.selfDevice,
        direction: "out",
        body: label,
        logicalCounter: createdAt,
        createdAt,
        status: "queued",
        contentType: CONTENT_TYPE_ATTACHMENT,
        viewOnce: opts?.viewOnce || undefined,
        disappearTimerSec: timer || undefined,
        expiresAt: expireAt(createdAt, timer),
      };
      await persistAndShow(msg, label);
      const out = await m.send({
        conversationId: deps.activeId,
        recipients: [contact],
        contentType: CONTENT_TYPE_ATTACHMENT,
        body: withDisappearSeconds(sealedBody, timer),
        eventId,
        createdAt,
        tracksMessage: true,
      });
      await deps.putMessage({ ...msg, status: out.status, transport: out.transport });
      markDelivery(eventId, out.status, out.transport);
      if (opts?.viewOnce) deps.setToast("View-once photo sent — it disappears after they open it");
    } catch (e) {
      deps.setToast(`Couldn't send attachment: ${e instanceof Error ? e.message : "error"}`);
    }
  }

  async function sendImage(file: File) {
    return sendAttachment(file, "image");
  }

  async function sendDocument(file: File) {
    return sendAttachment(file, "document");
  }

  async function sendViewOnceImage(file: File) {
    return sendAttachment(file, "image", { viewOnce: true });
  }

  async function sendStickerFile(file: File) {
    return sendAttachment(file, "sticker");
  }

  async function sendGif(file: File) {
    return sendAttachment(file, "gif");
  }

  /** Built-in sticker — only the tiny pack/id reference travels (recipient renders locally). */
  /** Text (or a sticker reference) to the active group: one id, pairwise copies. */
  async function sendGroupText(text: string, replyToEventId?: string) {
    const m = deps.messenger;
    if (!m || !deps.activeId || !deps.activeGroup) return;
    const eventId = createEventId();
    const createdAt = Date.now();
    const msg: MessageEvent = {
      eventId,
      conversationId: deps.activeId,
      senderDevice: m.selfDevice,
      direction: "out",
      body: text,
      logicalCounter: createdAt,
      createdAt,
      status: "queued",
      contentType: CONTENT_TYPE_GROUP,
      replyToEventId: replyToEventId,
    };
    await persistAndShow(msg, text);
    deps.setReplyTo(null);
    const out = await fanoutGroupMessage({
      group: deps.activeGroup,
      plaintext: text,
      contacts: deps.contacts,
      messenger: m,
      eventId,
      createdAt,
      replyToEventId: msg.replyToEventId,
    });
    await deps.putMessage({ ...msg, status: out.status, transport: out.transport });
    markDelivery(eventId, out.status, out.transport);
  }

  async function sendSticker(packId: string, stickerId: string) {
    const m = deps.messenger;
    if (!m || !deps.activeId) return;
    if (deps.activeConv?.type === "group") {
      if (deps.activeConv.requestPending) {
        deps.setToast("Join the group to write in it");
        return;
      }
      // Groups carry built-in stickers as their text reference (the phone does the same).
      await sendGroupText(stickerRefLabel(packId, stickerId));
      return;
    }
    const contact = directTarget();
    if (!contact) return;
    try {
      const label = stickerRefLabel(packId, stickerId);
      const timer = deps.disappearTimer;
      const eventId = createEventId();
      const createdAt = Date.now();
      const msg: MessageEvent = {
        eventId,
        conversationId: deps.activeId,
        senderDevice: m.selfDevice,
        direction: "out",
        body: label,
        logicalCounter: createdAt,
        createdAt,
        status: "queued",
        contentType: CONTENT_TYPE_ATTACHMENT,
        disappearTimerSec: timer || undefined,
        expiresAt: expireAt(createdAt, timer),
      };
      await persistAndShow(msg, label);
      const out = await m.send({
        conversationId: deps.activeId,
        recipients: [contact],
        contentType: CONTENT_TYPE_ATTACHMENT,
        body: withDisappearSeconds(stickerWireBody(packId, stickerId), timer),
        eventId,
        createdAt,
        tracksMessage: true,
      });
      await deps.putMessage({ ...msg, status: out.status, transport: out.transport });
      markDelivery(eventId, out.status, out.transport);
    } catch (e) {
      deps.setToast(`Couldn't send sticker: ${e instanceof Error ? e.message : "error"}`);
    }
  }

  async function openViewOnce(msg: MessageEvent) {
    if (!msg.viewOnce || msg.viewOnceOpenedAt) return;
    const url = deps.voiceUrls.get(msg.eventId);
    if (url) {
      URL.revokeObjectURL(url);
      deps.voiceUrls.delete(msg.eventId);
      deps.bumpVoice();
    }
    const burned: MessageEvent = {
      ...msg,
      viewOnceOpenedAt: Date.now(),
      body: "👁 View-once opened and removed",
      sealedJson: undefined,
    };
    await deps.putMessage(burned);
    deps.setMessages((m) => m.map((x) => (x.eventId === msg.eventId ? burned : x)));
    deps.setToast("View-once media removed from this device");
  }

  async function sendVoiceNote(blob: Blob, durationMs: number) {
    const m = deps.messenger;
    if (!m || !deps.activeId) return;
    const contact = directTarget();
    if (!contact) return;
    const check = validateAttachmentFile({
      fileName: `voice-${Date.now()}.webm`,
      mediaType: blob.type || "audio/webm",
      size: blob.size,
      kind: "audio",
    });
    if (!check.ok) {
      deps.setToast(check.message);
      return;
    }
    try {
      const { sealedBody, label } = await prepareVoiceNote(blob, durationMs);
      const timer = deps.disappearTimer;
      const eventId = createEventId();
      const createdAt = Date.now();
      deps.voiceUrls.set(eventId, URL.createObjectURL(blob));
      rememberMediaBlob(eventId, blob);
      deps.bumpVoice();
      const msg: MessageEvent = {
        eventId,
        conversationId: deps.activeId,
        senderDevice: m.selfDevice,
        direction: "out",
        body: label,
        logicalCounter: createdAt,
        createdAt,
        status: "queued",
        contentType: CONTENT_TYPE_ATTACHMENT,
        disappearTimerSec: timer || undefined,
        expiresAt: expireAt(createdAt, timer),
      };
      await persistAndShow(msg, label);
      const out = await m.send({
        conversationId: deps.activeId,
        recipients: [contact],
        contentType: CONTENT_TYPE_ATTACHMENT,
        body: withDisappearSeconds(sealedBody, timer),
        eventId,
        createdAt,
        tracksMessage: true,
      });
      await deps.putMessage({ ...msg, status: out.status, transport: out.transport });
      markDelivery(eventId, out.status, out.transport);
    } catch (e) {
      deps.setToast(`Couldn't send voice note: ${e instanceof Error ? e.message : "error"}`);
    }
  }

  return {
    sendMessage,
    sendReaction,
    deleteForMe,
    deleteForEveryone,
    forwardMessage,
    forwardTo,
    starMessage,
    pinMessage,
    tryOpenDirectPeer,
    sendImage,
    sendDocument,
    sendViewOnceImage,
    sendSticker,
    sendStickerFile,
    sendGif,
    openViewOnce,
    sendVoiceNote,
  };
}
